import { API_CONFIG } from '../services/apiConfig';

/**
 * Helper function to get backend origin URL
 * Removes /api suffix from BASE_URL to get the backend origin
 */
export const getBackendOrigin = (): string => {
  const apiBase = API_CONFIG.BASE_URL || '';
  return apiBase.replace(`/api/${API_CONFIG.VERSION || 'v1'}`, '');
};

/**
 * Prefix llm-server bakes into a tenant contract citation's `source_url`
 * (`/api/v1/files/stream/?document_id=...`, REV-2434). It is a fixed part of the
 * backend contract, not derived from this app's config -- the path is persisted in
 * message metadata, so it must not change with the frontend's API base.
 */
const CITATION_API_PATH_PREFIX = '/api/v1/';

/**
 * Resolves a citation's `source_url` into an href that works from this frontend.
 *
 * External policy citations carry absolute public URLs and pass through untouched.
 * Tenant contract citations carry a root-relative API path, which 404s against the
 * frontend's own origin whenever REACT_APP_API_BASE_URL points the backend at a
 * different host -- so the prefix is swapped for API_CONFIG.BASE_URL. That base
 * already ends in the version segment, hence replacing `/api/v1/` rather than
 * concatenating (which would yield `.../v1/api/v1/...`).
 *
 * Returns undefined for an empty value so callers keep rendering plain text
 * instead of a dead link.
 */
export const resolveCitationHref = (sourceUrl?: string): string | undefined => {
  const trimmed = sourceUrl?.trim();
  if (!trimmed) {
    return undefined;
  }

  // Absolute (scheme-qualified or protocol-relative): already browsable as-is.
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
    return trimmed;
  }

  const apiBase = (API_CONFIG.BASE_URL || '').replace(/\/+$/, '');
  if (apiBase && trimmed.startsWith(CITATION_API_PATH_PREFIX)) {
    return `${apiBase}/${trimmed.slice(CITATION_API_PATH_PREFIX.length)}`;
  }

  // Any other relative value: leave it alone rather than invent a base for it.
  return trimmed;
};

/**
 * Defers a hard navigation to allow React cleanup (effects, timers) to run.
 * Use for cross-origin redirects like backend login/logout.
 */
export const deferRedirect = (url: string, delayMs: number = 0): void => {
  // Schedule on macrotask queue to let React unmount/cleanup run first
  window.setTimeout(() => {
    window.location.assign(url);
  }, delayMs);
};

/**
 * Centralized login redirect function.
 * Uses deferRedirect for cross-origin navigation to allow React cleanup.
 */
export const redirectToLogin = (): void => {
  deferRedirect(`${API_CONFIG.BASE_URL}/auth/login`);
};

/**
 * Centralized logout redirect function.
 * Uses deferRedirect for cross-origin navigation to allow React cleanup.
 */
export const redirectToLogout = (): void => {
  deferRedirect(`${getBackendOrigin()}/logout`);
};
